import * as React from "react"

import { cn } from "@/lib/utils"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {
    containerClassName?: string
    /** The scroll container, e.g. to hand an IntersectionObserver as its root. */
    containerRef?: React.Ref<HTMLDivElement>
  }
>((
  { className, containerClassName, containerRef: forwardedContainerRef, ...props },
  ref
) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [isScrolled, setIsScrolled] = React.useState(false)

  // The scroll listener below needs its own handle, so the caller's ref is composed
  // onto the same node rather than replacing it.
  const setContainer = React.useCallback(
    (node: HTMLDivElement | null) => {
      containerRef.current = node
      if (typeof forwardedContainerRef === "function") {
        forwardedContainerRef(node)
      } else if (forwardedContainerRef) {
        // React writes RefObject.current itself, the React 18 types just mark it readonly
        const objectRef = forwardedContainerRef as React.MutableRefObject<HTMLDivElement | null>
        objectRef.current = node
      }
    },
    [forwardedContainerRef]
  )

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const update = () => setIsScrolled(container.scrollTop > 0)
    update()
    container.addEventListener("scroll", update, { passive: true })
    return () => container.removeEventListener("scroll", update)
  }, [])

  return (
    <div
      ref={setContainer}
      data-scrolled={isScrolled ? "true" : "false"}
      className={cn(
        "group/table-scroll relative w-full overflow-auto",
        containerClassName
      )}
    >
      <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  )
})
Table.displayName = "Table"

// Shadow instead of a border, since border-collapse won't repaint a collapsed border
// on a sticky thead while scrolling.
const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "[&_tr]:border-b bg-slate-50 sticky top-0 z-10",
      "group-data-[scrolled=true]/table-scroll:shadow-table-header",
      className
    )}
    {...props}
  />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium last:[&>tr]:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "py-3 px-4 text-left align-middle font-medium text-gray-700 text-sm [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props}
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "px-4 py-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className
    )}
    {...props}
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
